import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { useHierarchy } from "../../hooks/useHierarchy";
import { cn } from "../ui/utils";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "../ui/drawer";
import { Button } from "../ui/button";
import { Qualification, User as UserType } from "../../types";
import { formatDate, calculateQualificationStatus } from "../../utils/qualification-utils";
import { QualificationStatusBadge } from "./QualificationStatusBadge";
import { Badge } from "../ui/badge";
import { 
  Bell,
  X,
  Edit,
  Trash2,
  RotateCcw,
  Paperclip,
  FileText,
  Image,
  ExternalLink
} from "lucide-react";
import { Separator } from "../ui/separator";
import { DeleteQualificationDialog } from "./DeleteQualificationDialog";
import { toast } from "sonner";
import { useData } from "../../context/DataContext";
import { API_BASE } from "../../utils/api";
import { Can } from "../Can";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";

interface QualificationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  qualification: Qualification | null;
  viewOnly?: boolean;
}

export const QualificationDrawer: React.FC<QualificationDrawerProps> = ({
  open,
  onOpenChange,
  qualification,
  viewOnly = false
}) => {
  const { users, selectionUsers, deleteQualification } = useData();
  const { canManageUser } = useHierarchy();
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  if (!qualification) return null;

  const status = calculateQualificationStatus(qualification);

  // Helper to find recipient names and emails
  const getRecipientInfo = (id: string) => {
    // Try primary users list first
    let user = (users || []).find(u => u.id.toString() === id.toString());
    
    // If not found, try selectionUsers list (which contains all active/non-archived users)
    if (!user && selectionUsers) {
      user = (selectionUsers || []).find(u => u.id.toString() === id.toString());
    }

    return user ? `${user.name} (${user.email || 'No email'})` : id;
  };

  const getFileIcon = (mimetype?: string) => {
    if (mimetype?.includes('pdf')) return <FileText size={16} className="text-red-500" />;
    if (mimetype?.includes('image')) return <Image size={16} className="text-blue-500" />;
    return <Paperclip size={16} className="text-slate-400" />;
  };

  const handleAttachmentAction = async (attachment: { id: any; filename: string }, mode: 'view' | 'download') => {
    try {
      const token = localStorage.getItem("token");
      const actionUrl = `${API_BASE}/api/qualifications/attachments/${attachment.id}/download?mode=${mode}&format=json`;

      const response = await fetch(actionUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      let fileUrl: string;
      let filename = attachment.filename || 'download';
      let isBlobUrl = false;

      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        // S3-backed attachment: the backend returns a fresh signed URL generated
        // by the S3 helper. Never use the stored attachment.url — it is a
        // pre-signed URL created at upload time that expires after 1 hour.
        const result = await response.json();
        if (!response.ok || !result.success || !result.downloadUrl) {
          throw new Error(result?.message || `Failed to ${mode} attachment`);
        }
        fileUrl = result.downloadUrl;
        filename = result.filename || filename;
      } else {
        // Legacy local file: the backend streams the file directly
        if (!response.ok) throw new Error(`Failed to ${mode} attachment`);
        const blob = await response.blob();
        fileUrl = window.URL.createObjectURL(blob);
        isBlobUrl = true;
      }

      if (mode === 'view') {
        window.open(fileUrl, '_blank');
        // Note: blob URLs are not revoked immediately so the new tab can load
      } else {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', filename);
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        if (isBlobUrl) window.URL.revokeObjectURL(fileUrl);
      }
    } catch (error) {
      console.error(`${mode} failed:`, error);
      toast.error(`Failed to ${mode} file`);
    }
  };

  const handleDelete = async () => {
    if (qualification) {
      await deleteQualification(qualification.id);
      setIsDeleteDialogOpen(false);
      onOpenChange(false);
    }
  };

  const handleEdit = () => {
    if (qualification && qualification.person) {
      const personId = typeof qualification.person === 'object' ? qualification.person.id : qualification.person;
      navigate(`/people/${personId}/qualifications/edit/${qualification.id}`);
      onOpenChange(false);
    }
  };

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="h-full mt-0 rounded-none border-l sm:max-w-md">
        <div className="flex flex-col h-full bg-white">
          <DrawerHeader className="flex flex-row items-center justify-between px-6 py-4">
            <DrawerTitle className="text-xl font-bold text-slate-900">
              Qualification Details
            </DrawerTitle>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                <X size={18} className="text-slate-500" />
              </Button>
            </DrawerClose>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-8">
            {/* Employee Section */}
            <section className="space-y-1">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Employee</label>
              <div className="text-2xl font-medium text-slate-900">{qualification.person?.name}</div>
              <div className="text-sm text-slate-500">{qualification.person?.email || "No email provided"}</div>
            </section>

            <Separator className="bg-slate-100" />

            {/* Qualification Name & Type */}
            <section className="grid grid-cols-1 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Qualification Name</label>
                <div className="text-lg font-bold text-slate-900 leading-tight">{qualification.name}</div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Type</label>
                <div>
                  <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 font-medium px-3 py-0.5">
                    {typeof qualification.type === 'object' ? (qualification.type as any).label : qualification.type || "Other"}
                  </Badge>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Issuing Authority</label>
                <div className="text-base text-slate-700">{qualification.issuingAuthority || "N/A"}</div>
              </div>

              {/* Grid Details */}
              <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                <StaticDetail label="Issue Date" value={formatDate(qualification.issueDate)} />
                <StaticDetail 
                  label="Expiry Date" 
                  value={qualification.expiryDate ? formatDate(qualification.expiryDate) : "No expiry"} 
                  valueClassName={!qualification.expiryDate ? "text-slate-400 italic" : "text-slate-900"}
                />
                
                <StaticDetail label="Course Number" value={qualification.courseNumber || "N/A"} />
                <StaticDetail label="Course Provider" value={qualification.provider || "N/A"} />
                
                <StaticDetail label="Contact Ph" value={qualification.contactPhone || "N/A"} />
                <StaticDetail label="Cost (ex taxes)" value={qualification.cost ? `$${qualification.cost.toLocaleString()}` : "N/A"} />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Status</label>
                <div>
                  <QualificationStatusBadge status={status} className="px-3" />
                </div>
              </div>
            </section>

            <Separator className="bg-slate-100" />

            {/* Reminder Schedule */}
            <section className="space-y-4 pb-10">
              <div className="flex items-center gap-2 text-slate-400">
                <Bell size={16} />
                <span className="text-xs font-medium uppercase tracking-wider">Reminder Schedule</span>
              </div>
              
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Reminders will be sent:</p>
                <div className="flex flex-wrap gap-2">
                  {qualification.reminderIntervals && qualification.reminderIntervals.length > 0 ? (
                    qualification.reminderIntervals.map(interval => (
                      <Badge key={interval} variant="secondary" className="bg-slate-100 text-slate-600 font-normal px-2 py-1 rounded-md border-0">
                        {interval} days before expiry
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-slate-400 italic">No reminders configured</span>
                  )}
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Recipients:</p>
                <div className="space-y-1">
                  {qualification.reminderRecipients?.length ? (
                    qualification.reminderRecipients.map(id => (
                      <div key={id} className="text-sm text-slate-700">
                        {getRecipientInfo(id)}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-slate-400 italic">No specific recipients (System default)</div>
                  )}
                </div>
              </div>
            </section>

            <Separator className="bg-slate-100" />

            {/* Evidence & Attachments */}
            <section className="space-y-4 pb-10">
              <div className="flex items-center gap-2 text-slate-400">
                <Paperclip size={16} className="text-indigo-400" />
                <span className="text-[10px] font-bold uppercase tracking-widest">Evidence & Attachments</span>
              </div>
              
              <div className="grid grid-cols-1 gap-2">
                {qualification.attachments && qualification.attachments.length > 0 ? (
                  qualification.attachments.map((att) => {
                    const personId = typeof qualification.person === 'object' ? qualification.person.id : qualification.person;
                    const canInteract = canManageUser(personId) || ability.can(ACTIONS.READ, RESOURCES.PERSON_DETAIL_PAGE);
                    
                    return (
                      <div 
                        key={att.id} 
                        className={cn(
                          "group flex items-center justify-between p-2 rounded-xl border border-slate-100 bg-white transition-all duration-200",
                          canInteract ? "hover:border-indigo-100 hover:bg-indigo-50/20" : "opacity-80"
                        )}
                      >
                        <div 
                          className={cn(
                            "flex items-center gap-3 flex-1",
                            canInteract ? "cursor-pointer" : "cursor-default"
                          )}
                          onClick={() => canInteract && handleAttachmentAction(att, 'view')}
                          title={canInteract ? `View ${att.filename}` : "You don't have permission to view this attachment"}
                        >
                          <div className={cn(
                            "p-2 rounded-lg bg-slate-50 transition-colors",
                            canInteract && "group-hover:bg-white shadow-sm"
                          )}>
                            {getFileIcon(att.mimetype)}
                          </div>
                          <div className="flex flex-col">
                            <span className={cn(
                              "text-sm font-semibold text-slate-700 truncate max-w-[180px]",
                              canInteract && "group-hover:text-indigo-600"
                            )}>
                              {att.filename}
                            </span>
                            <span className="text-[10px] text-slate-400 font-medium">
                              {att.size ? `${(att.size / 1024).toFixed(1)} KB` : "N/A"} {canInteract ? "• Click to view" : ""}
                            </span>
                          </div>
                        </div>
                        
                        {canInteract && (
                          <div className="flex items-center gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-white shadow-none"
                              onClick={() => handleAttachmentAction(att, 'view')}
                              title="Preview"
                            >
                              <ExternalLink size={14} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-white shadow-none"
                              onClick={() => handleAttachmentAction(att, 'download')}
                              title="Download"
                            >
                              <Paperclip size={14} className="rotate-45" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-100 rounded-2xl bg-slate-50/50">
                    <Paperclip size={32} className="text-slate-300 mb-2" />
                    <p className="text-sm text-slate-400 font-medium">No documents attached</p>
                  </div>
                )}
              </div>
            </section>
          </div>

          {!viewOnly && (
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
              {qualification.deleted ? (
                <Button 
                  variant="outline" 
                  className="gap-2 border-slate-200 text-slate-700 hover:bg-white hover:text-indigo-600"
                >
                  <RotateCcw size={16} />
                  <span>Restore Qualification</span>
                </Button>
              ) : (
                <>
                  <Can I={ACTIONS.DELETE} a={RESOURCES.QUALIFICATION}>
                    <Button 
                      variant="ghost" 
                      className="gap-2 text-slate-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      <Trash2 size={16} />
                      <span>Delete</span>
                    </Button>
                  </Can>
                  <Can I={ACTIONS.UPDATE} a={RESOURCES.QUALIFICATION}>
                    <Button 
                      className="gap-2 bg-slate-900 hover:bg-slate-800 text-white"
                      onClick={handleEdit}
                    >
                      <Edit size={16} />
                      <span>Edit Qualification</span>
                    </Button>
                  </Can>
                </>
              )}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>

    <DeleteQualificationDialog 
      open={isDeleteDialogOpen}
      onOpenChange={setIsDeleteDialogOpen}
      onConfirm={handleDelete}
    />
    </>
  );
};

interface StaticDetailProps {
  label: string;
  value: string;
  valueClassName?: string;
}

const StaticDetail: React.FC<StaticDetailProps> = ({ label, value, valueClassName }) => (
  <div className="space-y-1">
    <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider leading-none">{label}</div>
    <div className={`text-sm truncate ${valueClassName || "text-slate-900"}`}>{value}</div>
  </div>
);